/**
 * Module codes and the three rendering states.
 *
 * Mirrors the backend registry in `server/app/core/modules.py`. These strings
 * are persisted server-side — never rename one.
 *
 * ## The two orthogonal axes
 *
 * Every module has two independent facts about it, and they must never be
 * collapsed into one value:
 *
 * | Axis                  | Owner       | Question                      | Lifetime  |
 * |-----------------------|-------------|-------------------------------|-----------|
 * | Entitlement           | per tenant  | "Is this in their plan?"       | permanent |
 * | Platform availability | fleet-wide  | "Is this currently working?"    | temporary |
 *
 * Effective access = entitled AND available — but the two states look
 * *different* to the user, which is the whole point:
 *
 * | State                        | Sidebar             | Page                      | Actions                       |
 * |------------------------------|---------------------|---------------------------|-------------------------------|
 * | Not entitled                 | invisible           | Unauthorized              | —                             |
 * | Entitled, platform-disabled  | visible, clickable  | loads, existing data shows | disabled + "temporarily unavailable" |
 * | Entitled, available          | visible             | normal                    | normal                        |
 *
 * The middle state is deliberately NOT "hidden". A tenant who used a tool
 * yesterday must not watch it silently vanish; they keep everything they
 * generated and lose only the ability to generate more.
 *
 * ## The client does not walk the module tree
 *
 * A module can be part of another one. No entry is today — compliance
 * extraction was folded into compliance — but the server resolves the tree
 * either way, once, and sends the resolved lists. `enabledModules` and
 * `unavailableModules` are already effective values.
 *
 * ## Not entitled means no fetch
 *
 * Hiding a surface is not enough. A component that mounts and returns early
 * still runs its effects, so it still calls an endpoint that answers 403.
 * Use `<ModuleGate>`, which renders nothing at all — see `ModuleGate.jsx`.
 */

export const MODULES = {
  // Always on. Every tenant has these — the product does not run without
  // them. Listed because the usage report labels them.
  AI_RESUME_EXTRACTION: "ai.resume_extraction",
  AI_RESUME_REUPLOAD: "ai.resume_reupload",
  AI_CANDIDATE_MATCH: "ai.candidate_match",

  // Sold per tenant. Every one of these has at least one gated surface in
  // `moduleSurfaces.js`.
  AI_CANDIDATE_JOB_FIT: "ai.candidate_job_fit",
  AI_INTERVIEW_QUESTIONS: "ai.interview_questions",
  AI_BOOLEAN_SEARCH: "ai.boolean_search",
  AI_JD_IMPROVER: "ai.jd_improver",
  // Compliance is one module. AI document extraction comes with it — there
  // was never a way to reach extraction except through a compliance screen.
  COMPLIANCE: "compliance",
  BRANDING: "branding",
  BULK_INTAKE: "bulk_intake",
  INTERVIEW_SCHEDULING: "interview_scheduling",

  // Outreach. OUTREACH is the parent — templates, style guides, sequences,
  // inbox, communication center. The two channel-group children gate which
  // channels a tenant can actually send through; a child resolves entitled
  // only while OUTREACH does too.
  OUTREACH: "outreach",
  OUTREACH_EMAIL: "outreach.email",
  // WhatsApp and Telegram are sold and toggled together.
  OUTREACH_CHAT: "outreach.chat_channels",
  // CV formatting — a recruiter picks a branded template and exports a
  // candidate's CV into it.
  RESUME_TEMPLATE: "resume_template",
  // Sourcing campaigns, the platform fetchers, enrichment and the scraper.
  SOURCING: "sourcing",
  // The Chromium extension. Separate from SOURCING: a tenant can have
  // campaigns without buying the per-seat browser tool.
  EXTENSION: "extension",
  // Users send product feedback to the platform team.
  FEEDBACK: "feedback",
};

/**
 * Guardian switch keys — mirror `app/core/tenant_config.py`.
 *
 * One switch. The per-flow switches were removed: a tenant admin has no way
 * to judge which content flow deserves screening.
 *
 * **Not a module.** `guardian_settings` was a registry module until 2026-08-10.
 * It said the same thing as the policy below, only weaker, so every tenant has
 * a guardian card now and the policy decides who owns the switch on it.
 */
export const GUARDIAN_SWITCHES = {
  MASTER: "master",
};

/**
 * Who decides the guardian switch. Set at platform level and per tenant, and
 * **the platform level wins** — a fleet-wide `FORCED_ON`/`FORCED_OFF` is a
 * floor, not a default, so no per-tenant override can lift it. Resolution is
 * the server's, in `app/services/guardian_config.py`.
 */
export const GUARDIAN_POLICIES = {
  TENANT_CHOICE: "TENANT_CHOICE",
  FORCED_ON: "FORCED_ON",
  FORCED_OFF: "FORCED_OFF",
};

export const GUARDIAN_POLICY_LABELS = {
  [GUARDIAN_POLICIES.TENANT_CHOICE]: "Tenant decides",
  [GUARDIAN_POLICIES.FORCED_ON]: "Forced on",
  [GUARDIAN_POLICIES.FORCED_OFF]: "Forced off",
};

/** The three states a module can be in for the current user. */
export const MODULE_STATE = {
  NOT_ENTITLED: "not_entitled",
  UNAVAILABLE: "unavailable",
  AVAILABLE: "available",
};

/**
 * Is the tenant entitled to `moduleCode`?
 *
 * `enabledModules` null/undefined means the server did not tell us — an older
 * backend, or the SuperAdmin payload which has no tenant. Fail OPEN, matching
 * the server: a gap in this list must never make a paying client's features
 * disappear. Enforcement is server-side and unconditional regardless.
 */
export const isModuleEnabled = (moduleCode, enabledModules) => {
  if (!moduleCode) return true;
  if (!Array.isArray(enabledModules)) return true;
  return enabledModules.includes(moduleCode);
};

/** Is `moduleCode` currently operational fleet-wide? */
export const isModuleAvailable = (moduleCode, unavailableModules) => {
  if (!moduleCode) return true;
  if (!Array.isArray(unavailableModules)) return true;
  return !unavailableModules.includes(moduleCode);
};

/**
 * Resolve a module to one of MODULE_STATE.
 *
 * SuperAdmins bypass entitlement (they administer every tenant) but NOT
 * availability — the kill switch exists precisely because the tool is believed
 * broken, and showing a SuperAdmin a working button the backend will refuse
 * would be a lie.
 */
export const getModuleState = (moduleCode, user) => {
  if (!moduleCode) return MODULE_STATE.AVAILABLE;

  const entitled = user?.isSuperAdmin ? true : isModuleEnabled(moduleCode, user?.enabledModules);
  if (!entitled) return MODULE_STATE.NOT_ENTITLED;

  if (!isModuleAvailable(moduleCode, user?.unavailableModules)) {
    return MODULE_STATE.UNAVAILABLE;
  }
  return MODULE_STATE.AVAILABLE;
};

/** Copy for the middle state. One wording, used everywhere it appears. */
export const MODULE_UNAVAILABLE_MESSAGE = "This feature is temporarily unavailable. Your existing data is unaffected — please try again later.";

export const MODULE_NOT_ENABLED_MESSAGE = "This feature is not enabled for your organization.";
